// /src/pages/admin/Dashboard.tsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  useDisclosure,
  useToast,
  Progress,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Flex,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { IProyecto } from '../../types';
import { mockProyectos } from '../../mocks/proyectos';
import { mockClientes } from '../../mocks/clientes';
import { mockEstandares } from '../../mocks/estandares';
import { mockUsuarios } from '../../mocks/usuarios';

export const AdminDashboard: React.FC = () => {
  const [proyectos, setProyectos] = useState<IProyecto[]>(mockProyectos);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [estandarSeleccionado, setEstandarSeleccionado] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  const calcularProgreso = (proyecto: IProyecto): number => {
    const total = proyecto.lineamientos.length;
    if (total === 0) return 0;
    const cumple = proyecto.lineamientos.filter(l => l.estado === 'Cumple').length;
    return Math.round((cumple / total) * 100);
  };

  // Calcular estadísticas
  const estadisticas = useMemo(() => {
    const totalProyectos = proyectos.length;
    const proyectosActivos = proyectos.filter(p => p.estadoGeneral !== 'Cerrado').length;
    const proyectosCerrados = proyectos.filter(p => p.estadoGeneral === 'Cerrado').length;
    const totalClientes = mockClientes.length;
    const totalUsuarios = mockUsuarios.length;
    const progresoPromedio = proyectos.length > 0
      ? Math.round(proyectos.reduce((acc, p) => acc + calcularProgreso(p), 0) / proyectos.length)
      : 0;

    return {
      proyectosActivos,
      proyectosCerrados,
      totalClientes,
      totalUsuarios,
      progresoPromedio,
    };
  }, [proyectos]);

  const handleCrearProyecto = () => {
    if (!clienteSeleccionado || !estandarSeleccionado) {
      toast({
        title: 'Error',
        description: 'Selecciona un cliente y un estándar',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const cliente = mockClientes.find(c => c.id === clienteSeleccionado);
    const estandar = mockEstandares.find(e => e.id === estandarSeleccionado);

    if (!cliente || !estandar) return;

    const lineamientos = estandar.capitulos.flatMap(cap =>
      cap.lineamientos.map(lin => ({ ...lin, comentarios: [] }))
    );

    const nuevoProyecto: IProyecto = {
      id: `proyecto-${Date.now()}`,
      empresaClienteId: cliente.id,
      estandarId: estandar.id,
      nombreEstandar: estandar.nombre,
      nombreEmpresa: cliente.nombre,
      estadoGeneral: 'Iniciado',
      lineamientos,
    };

    setProyectos([...proyectos, nuevoProyecto]);
    
    toast({
      title: 'Proyecto creado',
      description: `Proyecto para ${cliente.nombre} creado exitosamente`,
      status: 'success',
      duration: 3000,
    });

    setClienteSeleccionado('');
    setEstandarSeleccionado('');
    onClose();
  };

  const getEstadoBadge = (estado: string) => {
    const colorScheme = {
      'Iniciado': 'blue',
      'En Auditoría': 'yellow',
      'Cerrado': 'green',
    }[estado] || 'gray';

    return <Badge colorScheme={colorScheme}>{estado}</Badge>;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="lg">Dashboard</Heading>
        <Button colorScheme="blue" onClick={onOpen}>
          + Nuevo Proyecto
        </Button>
      </Box>

      {/* Paneles de Estadísticas */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
        {/* Proyectos Activos */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" position="relative">
          <Flex justify="space-between" align="start">
            <Box>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Proyectos Activos
              </Text>
              <Text fontSize="3xl" fontWeight="bold" mb={1}>
                {estadisticas.proyectosActivos}
              </Text>
              <Text fontSize="xs" color="green.500">
                ↑ {estadisticas.progresoPromedio}% progreso promedio
              </Text>
            </Box>
            <Box bg="blue.500" p={3} borderRadius="md">
              <Text fontSize="2xl">📊</Text>
            </Box>
          </Flex>
        </Box>

        {/* Proyectos Cerrados */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" position="relative">
          <Flex justify="space-between" align="start">
            <Box>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Proyectos Cerrados
              </Text>
              <Text fontSize="3xl" fontWeight="bold" mb={1}>
                {estadisticas.proyectosCerrados}
              </Text>
              <Text fontSize="xs" color="green.500">
                ✓ Completados exitosamente
              </Text>
            </Box>
            <Box bg="green.500" p={3} borderRadius="md">
              <Text fontSize="2xl">✅</Text>
            </Box>
          </Flex>
        </Box>

        {/* Clientes Activos */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" position="relative">
          <Flex justify="space-between" align="start">
            <Box>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Clientes Activos
              </Text>
              <Text fontSize="3xl" fontWeight="bold" mb={1}>
                {estadisticas.totalClientes}
              </Text>
              <Text fontSize="xs" color="blue.500">
                🏢 Empresas registradas
              </Text>
            </Box>
            <Box bg="purple.500" p={3} borderRadius="md">
              <Text fontSize="2xl">👥</Text>
            </Box>
          </Flex>
        </Box>

        {/* Total Usuarios */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" position="relative">
          <Flex justify="space-between" align="start">
            <Box>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Total Usuarios
              </Text>
              <Text fontSize="3xl" fontWeight="bold" mb={1}>
                {estadisticas.totalUsuarios}
              </Text>
              <Text fontSize="xs" color="orange.500">
                👤 Usuarios del sistema
              </Text>
            </Box>
            <Box bg="orange.500" p={3} borderRadius="md">
              <Text fontSize="2xl">📈</Text>
            </Box>
          </Flex>
        </Box>
      </SimpleGrid>

      {/* Tabla de Proyectos */}
      <Box mb={4}>
        <Heading size="md" mb={4}>Proyectos Recientes</Heading>
      </Box>
      
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Empresa</Th>
              <Th>Estándar</Th>
              <Th>Progreso</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {proyectos.map((proyecto) => {
              const progreso = calcularProgreso(proyecto);
              return (
                <Tr key={proyecto.id}>
                  <Td fontWeight="medium">{proyecto.nombreEmpresa}</Td>
                  <Td>
                    <Text fontSize="sm" noOfLines={1}>
                      {proyecto.nombreEstandar}
                    </Text>
                  </Td>
                  <Td>
                    <Box>
                      <Progress value={progreso} colorScheme="blue" size="sm" mb={1} />
                      <Text fontSize="xs" color="gray.600">{progreso}%</Text>
                    </Box>
                  </Td>
                  <Td>{getEstadoBadge(proyecto.estadoGeneral)}</Td>
                  <Td>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => navigate(`/proyecto/${proyecto.id}`)}
                    >
                      Ver Detalles
                    </Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Modal para crear proyecto */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Crear Nuevo Proyecto</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Cliente</FormLabel>
              <Select
                placeholder="Selecciona un cliente"
                value={clienteSeleccionado}
                onChange={(e) => setClienteSeleccionado(e.target.value)}
              >
                {mockClientes.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Estándar</FormLabel>
              <Select
                placeholder="Selecciona un estándar"
                value={estandarSeleccionado}
                onChange={(e) => setEstandarSeleccionado(e.target.value)}
              >
                {mockEstandares.map((estandar) => (
                  <option key={estandar.id} value={estandar.id}>
                    {estandar.nombre}
                  </option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleCrearProyecto}>
              Crear Proyecto
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
